import { UserDTO } from 'app/auth/store/type';
import { ObjectId } from 'app/common.types';
import { ROUTER_API } from 'app/constants/api.constant';
import { ListObject } from 'app/main/design-system/models/listObjectsModel';
import BaseApi from 'app/services/api/base.api';
import { FranchiseFeatureType, FranchiseInDTO, FranchisePartnerDTO } from '../../franchises/type';
import { LoyaltyModelEntity, LoyaltyType } from '../../loyalties/type';
import { StoreItemDTO } from '../../stores/type';
import {
  CreateMerchantForm,
  OwnerDTO,
  StoreOwnerAccessDTO,
  StoreUberallLinkDTO,
  UberallInDTO,
  UberallProvisioningLocationDTO,
  UberallProvisioningLocationListDTO,
  UberallUserDTO,
  UberallUserListOutDTO,
} from '../types/type';

export const adminApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token' });

export const updateSubscriptionAPI = async (userId: string, subscriptionStatus: boolean) => {
  if (subscriptionStatus) {
    const response = await adminApi.api.patch(`/api/v2/users/${userId}/activate-subscription`, {});
    return response.data;
  }
  const response = await adminApi.api.patch(`/api/v2/users/${userId}/deactivate-subscription`, {});
  return response.data;
};

export const updateFranchiseFeatureStatusAPI = async (
  franchiseId: string,
  feature: FranchiseFeatureType,
  isEnabled: boolean,
) => {
  const response = await adminApi.api.patch<FranchiseFeatureType[]>(`/api/v2/franchises/${franchiseId}/features`, {
    feature,
    isEnabled,
  });
  return response.data;
};

export const createMerchantApi = async (merchant: CreateMerchantForm) => {
  const { data } = await adminApi.api.post<UserDTO>('/api/v2/franchise-owner', merchant);
  return data;
};

export const getFranchiseApi = async (franchiseId: string) => {
  const { data } = await adminApi.api.get<FranchiseInDTO>(`/api/v2/franchises/${franchiseId}`);
  return data;
};

export const patchSenderIdApi = async (franchiseId: string, senderId: string) => {
  const { data } = await adminApi.api.patch<FranchiseInDTO>(`/api/v2/franchises/${franchiseId}/sender-id`, {
    senderId,
  });
  return data;
};

export const getFranchisePartnerApi = async (franchiseId: string) => {
  const { data } = await adminApi.api.get<FranchisePartnerDTO>(`/api/v2/franchises/${franchiseId}/partner`);
  return data;
};

export const patchFranchisePartnerApi = async (franchiseId: string, isPartner: boolean) => {
  const { data } = await adminApi.api.patch<FranchisePartnerDTO>(`/api/v2/franchises/${franchiseId}/partner`, {
    isPartner,
  });
  return data;
};

export const getMinimalStoreListApi = async (franchiseId: string) => {
  const { data } = await adminApi.api.get<StoreItemDTO[]>(`/api/v2/franchises/${franchiseId}/stores/list`);
  return data;
};

export const patchLoyaltyTypeApi = async (loyaltyModelId: string, loyaltyType: LoyaltyType) => {
  const { data } = await adminApi.api.patch<LoyaltyModelEntity>(`/api/v2/loyalties/${loyaltyModelId}/loyalty-type`, {
    loyaltyType,
  });
  return data;
};

export const deactivateFranchiseApi = async (franchiseId: string) => {
  const { data } = await adminApi.api.patch<FranchiseInDTO>(`/api/v2/franchises/${franchiseId}/deactivate`, {});
  return data;
};

export const getOwnersApi = async (franchiseId: ObjectId): Promise<ListObject<OwnerDTO>> => {
  const response = await adminApi.api.get<ListObject<OwnerDTO>>(`/api/v2/users/owners`, {
    params: { franchiseId },
  });
  return response.data;
};

export const getUberallUsersApi = async (franchiseId: ObjectId, query: string): Promise<UberallUserDTO[]> => {
  const response = await adminApi.api.get<UberallUserListOutDTO>(`/services/v2/uberall/users`, {
    params: { franchiseId, query, max: 50, offset: 0 },
  });
  return response.data?.users ?? [];
};

export const getUberallTokenApi = async (email: string): Promise<string> => {
  const response = await adminApi.api.get<{ token: string }>(`/services/v2/uberall/token`, { params: { email } });
  return response.data.token;
};

export const postUberallUserInApi = async (
  uberallInDTO: { id: ObjectId; email: string; token: string },
  userId: ObjectId,
): Promise<UberallInDTO> => {
  const response = await adminApi.api.post<UberallInDTO>(`/api/v2/users/${userId}/uberall`, uberallInDTO);
  return response.data;
};

export const deleteUberallUserApi = async (userId: ObjectId) => {
  await adminApi.api.delete<null>(`/api/v2/users/${userId}/uberall`);
};

const buildFranchiseWideAccessUrl = (franchiseId: ObjectId, userId: ObjectId) =>
  `/api/v2/admin/franchises/${franchiseId}/store-owners/${userId}/franchise-wide-access`;

export const getStoreOwnersAccessApi = async (franchiseId: ObjectId): Promise<StoreOwnerAccessDTO[]> => {
  const response = await adminApi.api.get<StoreOwnerAccessDTO[]>(
    `/api/v2/admin/franchises/${franchiseId}/store-owners`,
  );
  return response.data;
};

export const grantFranchiseWideAccessApi = async (
  franchiseId: ObjectId,
  userId: ObjectId,
): Promise<StoreOwnerAccessDTO> => {
  const response = await adminApi.api.post<StoreOwnerAccessDTO>(buildFranchiseWideAccessUrl(franchiseId, userId));
  return response.data;
};

export const revokeFranchiseWideAccessApi = async (
  franchiseId: ObjectId,
  userId: ObjectId,
): Promise<StoreOwnerAccessDTO> => {
  const response = await adminApi.api.delete<StoreOwnerAccessDTO>(buildFranchiseWideAccessUrl(franchiseId, userId));
  return response.data;
};

export const getUberallProvisioningLocationsApi = async (query: string): Promise<UberallProvisioningLocationDTO[]> => {
  const response = await adminApi.api.get<UberallProvisioningLocationListDTO>(
    `/services/v2/uberall/provisioning/locations`,
    { params: { query, max: 50, offset: 0 } },
  );
  return response.data?.locations ?? [];
};

/** By id, because the search route matches name and address only — an id returns no rows. */
export const getUberallProvisioningLocationApi = async (
  locationId: number,
): Promise<UberallProvisioningLocationDTO> => {
  const response = await adminApi.api.get<UberallProvisioningLocationDTO>(
    `/services/v2/uberall/provisioning/locations/${locationId}`,
  );
  return response.data;
};

export const getStoreUberallLinkApi = async (storeId: ObjectId): Promise<StoreUberallLinkDTO> => {
  const response = await adminApi.api.get<StoreUberallLinkDTO>(`/api/v2/stores/${storeId}/uberall`);
  return response.data;
};

export const patchStoreUberallLinkApi = async (
  storeId: ObjectId,
  patch: Partial<Pick<StoreUberallLinkDTO, 'locationId' | 'reportRecipientEmail'>>,
): Promise<StoreUberallLinkDTO> => {
  const response = await adminApi.api.patch<StoreUberallLinkDTO>(`/api/v2/stores/${storeId}/uberall`, patch);
  return response.data;
};
